import {html} from "lit";
import {DateTime} from "luxon";
import {container, singleton} from "tsyringe";
import {appState} from "../app.state";
import {BackendClient} from "../connectivity/backend-client";
import {NOTIFICATION_DURATION} from "../constants";
import {UiHelper} from "../helpers/ui.helper";
import {StringHelper} from "../helpers/string.helper";
import {ExecutionVerification} from "./execution-verification.type";
import {Entity} from "../domain/entities/entity";
import {ArrayHelper} from "../helpers/array.helper";
import {buildUserInfo} from "../authentication/build-user-info";

type AuthResponse = {
    reason: string
    reasonCode: number
    success: boolean
    employeeUid: string | null
    /**
     * The logged-in user's OWN uid, transport spelling. The backend writes it in
     * the success overlay only, so a rejected login does not carry it — and
     * `employeeUid` beside it is the linked EMPLOYEE's uid, a different
     * identifier that this field is deliberately not folded into.
     */
    userUid?: string | null
    /**
     * The server's echo of the resolved username. Declared here because the
     * factory builds `UserInfo` from the RESPONSE alone; before this the login
     * path read the username from the form input instead, which differs
     * whenever the user typed a different case — the backend resolves the user
     * case-insensitively, so `ALICE` is echoed back as `alice`.
     */
    username?: string
    privileges: string[]
    token: {
        hash: string | null
        validUntil: string
    }
}


type ExecutionVerificationRequest = {
    entity: Entity | null
    privilege: string
}

type ExecutionVerificationResponse = {
    entity: string,
    uid: string,
    privilege: string,
    canExecute: boolean
}

@singleton()
export class AuthorizationService {

    private _authorizationStorage: Map<Entity | null, Map<string, PromiseWithResolvers<ExecutionVerification>>> = new Map<Entity | null, Map<string, PromiseWithResolvers<ExecutionVerification>>>;

    public async login(username: string, password: string): Promise<boolean> {
        const result = await container.resolve(BackendClient).post<AuthResponse>(`/api/v1/auth/login`, false, {
            username,
            password
        });

        if (!result || !result.success) {
            UiHelper.notify({
                level: "error",
                duration: NOTIFICATION_DURATION,
                closable: true,
                title: `Login fehlgeschlagen`,
                content: html`<p>${result?.reason}</p>`
            })
            return false;
        }

        const validUntil = DateTime.fromISO(result.token.validUntil);
        if (!validUntil.isValid || validUntil < DateTime.utc()) {
            return false;
        }

        // Through the shared factory, never by hand — and from the RESPONSE,
        // not from `username` (the form input). The hydration path builds the
        // same object and returns early once a user is logged in, so the two
        // sites cannot cover for each other; only a single factory keeps them
        // from drifting the way `employeeUid` already has on the backend.
        appState.user = buildUserInfo(result);

        appState.privileges = result.privileges ?? [];

        return true;
    }

    public async logout(): Promise<void> {
        await container.resolve(BackendClient).post<void>(`/api/v1/auth/logout`);
        appState.clearUser();
        localStorage.clear();

        return appState.router.goto(`/auth/login`);
    }

    public hasPrivilege(privilege: string): boolean {
        // @todo - switch to "canExecute"
        return appState.privileges.length === 0 || appState.privileges.includes(privilege);
    }

    public canExecute(executionVerificationRequests: ExecutionVerification[]): Promise<ExecutionVerification[]> {
        const executingVerificationPromises = executionVerificationRequests.map(e => {
            if (this._authorizationStorage.has(e.entity) && this._authorizationStorage.get(e.entity)!.has(e.commandName)) {
                return this._authorizationStorage.get(e.entity)!.get(e.commandName)!.promise;
            }

            return null;
        }).filter(ArrayHelper.notEmpty<Promise<ExecutionVerification>>);

        const nonExistingExecutionVerificationRequests = executionVerificationRequests
            .filter(e => !this._authorizationStorage.has(e.entity) || !this._authorizationStorage.get(e.entity)!.has(e.commandName))
            .map(request => {
                return <ExecutionVerificationRequest>{
                    entity: request.entity,
                    privilege: request.commandName
                };
            });

        if (nonExistingExecutionVerificationRequests.length > 0) {
            this._canExecute(nonExistingExecutionVerificationRequests);
        }

        const newExecutingVerificationPromises = nonExistingExecutionVerificationRequests.map(request => {
            return this._authorizationStorage.get(request.entity)!.get(request.privilege)!.promise;
        });

        return Promise.all([
            ...executingVerificationPromises,
            ...newExecutingVerificationPromises
        ]);
    }

    private _canExecute(requests: ExecutionVerificationRequest[]): void {

        const executionVerificationPromiseWithResolvers = [];
        const executionVerificationRequests = requests.map(request => {

            const entity = request.entity ? StringHelper.toKebabCase(request.entity.constructor.name) : null;
            const uid = request.entity?.uid ?? null;
            const privilege = request.privilege;

            if (!this._authorizationStorage.has(request.entity)) {
                this._authorizationStorage.set(request.entity, new Map<string, PromiseWithResolvers<ExecutionVerification>>());
            }
            const authorizationsByEntity = this._authorizationStorage.get(request.entity)!;

            executionVerificationPromiseWithResolvers.push()
            authorizationsByEntity.set(request.privilege, Promise.withResolvers());

            return {entity, uid, privilege};
        })

        container
            .resolve(BackendClient)
            .post<Array<ExecutionVerificationResponse>>(
                '/api/v1/auth/has-privilege',
                false,
                {executionVerificationRequests}
            ).then(responses => {
            // HasPrivilegeAction always answers with a JSON data array, never with 204, so the
            // client's null (reserved for 204) does not occur here.
            for (const response of responses!) {
                const request = requests.find(r => (r.entity?.uid ?? null) === response.uid && r.privilege === response.privilege)!;
                const authorizationsByEntity = this._authorizationStorage.get(request.entity);
                const executionVerificationPromiseWithResolvers = authorizationsByEntity!.get(request.privilege)!;

                executionVerificationPromiseWithResolvers.resolve({
                    entity: request.entity,
                    commandName: request.privilege,
                    canExecute: response.canExecute
                });
            }
        });
    }
}